import React, { useEffect } from 'react';
import { Award, Globe, Users, TrendingUp, Shield, Heart } from 'lucide-react';
import Newsletter from '../components/Newsletter';
import { useTranslation } from 'react-i18next';

const About = () => {
  const { t } = useTranslation();
  
  useEffect(() => {
    document.title = 'About Us | Fundora Global';
  }, []);

  const values = [
    {
      icon: Shield,
      title: t('about.reliability'),
      description: t('about.reliabilityDesc')
    },
    {
      icon: Globe,
      title: t('about.globalReachValue'),
      description: t('about.globalReachValueDesc')
    },
    {
      icon: TrendingUp,
      title: t('about.growth'),
      description: t('about.growthDesc')
    },
    {
      icon: Heart,
      title: t('about.clientFocused'),
      description: t('about.clientFocusedDesc')
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#102131] to-[#1a3449] text-[#F7FDFF] py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            {t('about.title')}
          </h1>
          <p className="text-xl text-[#F7FDFF]/80 max-w-3xl">
            {t('about.subtitle')}
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-bold text-[#102131] mb-6">
                {t('about.ourMission')}
              </h2>
              <p className="text-lg text-gray-600 mb-4">
                {t('about.missionText1')}
              </p>
              <p className="text-lg text-gray-600 mb-4">
                {t('about.missionText2')}
              </p>
              <p className="text-lg text-gray-600">
                {t('about.missionText3')}
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1531971589569-0d9370cbe1e5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjB2aWxsYXxlbnwwfHx8fDE3NjYxNTEyMjZ8MA&ixlib=rb-4.1.0&q=85"
                alt="Luxury Property"
                className="rounded-xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-[#F7FDFF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#102131] mb-4">
              {t('about.coreValues')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('about.coreValuesSubtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <div
                  key={index}
                  className="group p-6 bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-[#CDA259]/10"
                >
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-[#CDA259]/10 rounded-full mb-4 group-hover:bg-[#CDA259]/20 transition-colors">
                    <Icon size={28} className="text-[#CDA259]" />
                  </div>
                  <h3 className="text-xl font-bold text-[#102131] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600">{value.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-[#102131] mb-8 text-center">
              {t('about.ourApproach')}
            </h2>
            
            <div className="space-y-8">
              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#CDA259] rounded-full flex items-center justify-center text-white font-bold">
                    1
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#102131] mb-2">{t('about.expertVerification')}</h3>
                  <p className="text-gray-600">
                    {t('about.expertVerificationDesc')}
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#CDA259] rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#102131] mb-2">{t('about.directPartnerships')}</h3>
                  <p className="text-gray-600">
                    {t('about.directPartnershipsDesc')}
                  </p>
                </div>
              </div>

              <div className="flex gap-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-[#CDA259] rounded-full flex items-center justify-center text-white font-bold">
                    3
                  </div>
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-[#102131] mb-2">{t('about.endToEndSupport')}</h3>
                  <p className="text-gray-600">
                    {t('about.endToEndSupportDesc')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#102131] to-[#1a3449] text-[#F7FDFF]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Award size={48} className="text-[#CDA259] mx-auto mb-6" />
          <h2 className="text-4xl font-bold mb-6">
            {t('about.investWithConfidence')}
          </h2>
          <p className="text-xl text-[#F7FDFF]/80 mb-8">
            {t('about.investWithConfidenceDesc')}
          </p>
        </div>
      </section>

      {/* Newsletter */}
      <Newsletter />
    </div>
  );
};

export default About;
